<template>
    <div v-if="!accept" class="p-5">
      Bạn chưa được phép xem trang này!
    </div>
    <div v-else class="product-list-by-project">
      <p v-if="isLoading" style="color: blue">Đang thao tác với dữ liệu...</p>
      
      <!-- Mobile Card View (shown on mobile screens via CSS) -->
      <div class="mobile-view">
        <div class="mobile-filters mb-3">
          <b-button @click="openFilterDrawer" variant="primary" size="sm" class="w-100">
            <b-icon icon="funnel"></b-icon> Bộ lọc
            <b-badge v-if="activeFiltersCount > 0" variant="light" class="ml-2">{{activeFiltersCount}}</b-badge>
          </b-button>
        </div>
        
        <div class="mobile-summary mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <span class="text-muted">Số lượng: {{totalCount}}</span>
          </div>
        </div>

        <!-- Mobile Cards -->
        <div v-if="isLoading" class="mobile-loading">
          <div class="text-center py-4">
            <b-spinner variant="primary"></b-spinner>
            <p class="mt-2">Đang tải dữ liệu...</p>
          </div>
        </div>
        
        <div v-else-if="mobileData.length === 0" class="mobile-empty">
          <div class="text-center py-4">
            <b-icon icon="inbox" font-scale="3" class="text-muted"></b-icon>
            <p class="mt-2 text-muted">Không có dữ liệu</p>
          </div>
        </div>
        
        <div v-else class="mobile-cards">
          <div v-for="item in mobileData" :key="item.id" class="mobile-card mb-3" :class="getRowVariant(item)">
            <div class="card-header d-flex justify-content-between align-items-start">
              <div class="card-title">
                <strong>{{item.address}}</strong>
                <small class="text-muted d-block">{{item.fullname}} - {{item.sale}}</small>
              </div>
              <div class="card-actions">
                <b-icon :icon="item.isfavored ? 'star-fill' : 'star'" 
                       @click="updateStar({id: item.id, isfavored: item.isfavored})" 
                       font-scale="1.2" 
                       class="clickable text-warning"></b-icon>
                <b-dropdown size="sm" variant="link" class="ml-2">
                  <template #button-content>
                    <b-icon icon="three-dots-vertical"></b-icon>
                  </template>
                  <b-dropdown-item @click="rowClicked({id: item.id})">Xem chi tiết</b-dropdown-item>
                  <b-dropdown-item @click="rowEditClicked({id: item.id})" v-if="can_user_edit()">Chỉnh sửa</b-dropdown-item>
                  <b-dropdown-item @click="menuClicked({menu_code: menu_item.code, id: item.id})" 
                                 v-for="menu_item in menu" :key="menu_item.code">
                    {{menu_item.text}}
                  </b-dropdown-item>
                  <b-dropdown-divider v-if="can_user_edit()"></b-dropdown-divider>
                  <b-dropdown-item @click="rowDeleteClicked({id: item.id})" class="text-danger" v-if="can_user_edit()">Xóa</b-dropdown-item>
                </b-dropdown>
              </div>
            </div>
            
            <div class="card-body">
              <div class="info-row">
                <span class="label">Loại:</span>
                <span class="value">{{item.type}}</span>
              </div>
              <div class="info-row">
                <span class="label">Đường:</span>
                <span class="value">{{item.street}}</span>
              </div>
              <div class="info-row" v-if="item.project">
                <span class="label">Dự án:</span>
                <span class="value">{{item.project}}</span>
              </div>
              <div class="info-row">
                <span class="label">Trạng thái:</span>
                <span class="value" :class="getStatusClass(item.status)">{{item.status}}</span>
              </div>
              <div class="info-row" v-if="item.price">
                <span class="label">Giá bán (tr):</span>
                <span class="value text-success font-weight-bold">{{formatPrice(item.price)}}</span>
              </div>
              <div class="info-row" v-if="item.priceForRent">
                <span class="label">Giá thuê:</span>
                <span class="value text-info">{{formatPrice(item.priceForRent)}} tr/tháng</span>
              </div>
              <div class="info-row" v-if="item.area">
                <span class="label">Diện tích:</span>
                <span class="value">{{item.area}} m²</span>
              </div>
              <div class="info-row" v-if="item.direction">
                <span class="label">Hướng:</span>
                <span class="value">{{item.direction}}</span>
              </div>
              <div class="info-row" v-if="item.date">
                <span class="label">Ngày tạo:</span>
                <span class="value">{{formatDate(item.date)}}</span>
              </div>
            </div>
            
            <div class="card-footer">
              <div class="d-flex justify-content-between align-items-center">
                <b-button size="sm" variant="outline-primary" @click="rowClicked({id: item.id})">
                  <b-icon icon="eye"></b-icon> Xem
                </b-button>
                <b-button size="sm" variant="outline-secondary" @click="rowEditClicked({id: item.id})" v-if="can_user_edit()">
                  <b-icon icon="pencil"></b-icon> Sửa
                </b-button>
                <b-button size="sm" variant="outline-success" @click="menuClicked({menu_code: 'view_posting', id: item.id})">
                  <b-icon icon="megaphone"></b-icon> Đăng tin
                </b-button>
                <b-button size="sm" variant="outline-info" @click="cellClicked({key: 'gps', value: item.gps})" v-if="item.gps">
                  <b-icon icon="geo-alt"></b-icon> Bản đồ
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- Mobile Pagination -->
        <div class="mobile-pagination mt-4">
          <b-pagination
            v-model="mobilePage"
            :total-rows="mobilePagination.total"
            :per-page="mobilePagination.limit"
            :limit="5"
            size="sm"
            aria-controls="my-table"
            class="justify-content-center"
          ></b-pagination>
        </div>
      </div>

      <!-- Desktop Table View (shown on desktop screens via CSS) -->
      <div class="desktop-view">
          <div class="desktop-filters mb-3">
            <b-button @click="openFilterDrawer" variant="outline-primary" size="sm">
              <b-icon icon="funnel"></b-icon> Bộ lọc
              <b-badge v-if="activeFiltersCount > 0" variant="primary" class="ml-2">{{activeFiltersCount}}</b-badge>
            </b-button>
          </div>
        
        <table-view
            :columns="columns"
            :display_columns="display_columns"
            :filters="filters"
            :restrict_filters="restrict_filters"
            :table_name="table_name"
            :sorts="sorts"
            @row-clicked="rowClicked"
            @row-edit-clicked="rowEditClicked"
            @row-delete-clicked="rowDeleteClicked"
            @update-group-clicked="updateGroupClicked"
            @delete-group-clicked="deleteGroupClicked"
            @cell-clicked="cellClicked"
            @menu-clicked="menuClicked"
            @star-clicked="updateStar"
            :rowPreprocessFunc="rowPreprocessFunc"
            :menu="menu"
            ref="tableview"
            :groupEditable="true"
            :itemEditable="true"
            :hasStars="true"
            :hideFilterButton="true"
        >
        </table-view>
      </div>

      <!-- Filter Drawer -->
      <filter-drawer
        :show="showFilterDrawer"
        :filters="filters"
        :columns="columns"
        :fallback-options="fallbackFilterOptions"
        @close="closeFilterDrawer"
        @clear-filters="clearAllFilters"
        @apply-filters="applyFilters"
      />
    </div>
</template>

<script>

import ProductTableMeta from '@/models/ProductTableMeta'
import GenericTableView from '@/views/base/GenericTableView'
import FilterDrawer from '@/views/base/FilterDrawer'

import ProductService from '@/services/productService'
import moment from 'moment'

export default {
  name: 'ProductListByProject',
  components: {
      'table-view': GenericTableView,
      'filter-drawer': FilterDrawer
  },
  data () {
    return {
        // display_columns: ProductTableMeta.list_views.columns,
        columns: ProductTableMeta.columns,
        filters: ProductTableMeta.list_views.filters,
        restrict_filters: [
            {
                col: 'project'
            }],
        table_name: ProductTableMeta.table_name,
        sorts: ProductTableMeta.list_views.sorts,
        menu: [
          {
            code: 'view_posting',
            text: 'Đăng tin'
          },
          {
            code: 'status_sold',
            text: 'Đã bán'
          },
          {
            code: 'status_stop',
            text: 'Ngưng bán'
          },
          {
            code: 'status_selling',
            text: 'Đang bán'
          },
          {
            code: 'status_selling_renting',
            text: 'Bán hoặc Cho thuê'
          },
          {
            code: 'status_hang',
            text: 'Treo bảng'
          },
          {
            code: 'priority_high',
            text: 'SP HOT'
          },
          {
            code: 'urgency_high',
            text: 'SP Bán gấp'
          }
        ],
        isLoading: false,
        mobileData: [],
        mobilePagination: {
          total: 0,
          limit: 20
        },
        mobilePage: 1,
        showFilterDrawer: false
    }
  },
  computed: {
    accept() {
      // if (this.$store.getters.role == 'ctv') {
      //   return false;
      // }
      return true;
    },

    totalCount() {
      return this.mobilePagination.total;
    },

    activeFiltersCount() {
      let count = 0;
      this.filters.forEach(filter => {
        if (filter.value) {
          if (Array.isArray(filter.value) && filter.value.length > 0) {
            count++;
          } else if (!Array.isArray(filter.value)) {
            count++;
          }
        }
        if (filter.from || filter.to) {
          count++;
        }
      });
      return count;
    },

    fallbackFilterOptions() {
      return {
        status: [
          { text: 'Đang bán', value: 'Đang bán' },
          { text: 'Đã bán', value: 'Đã bán' },
          { text: 'Ngưng bán', value: 'Ngưng bán' },
          { text: 'Bán hoặc Cho thuê', value: 'Bán hoặc Cho thuê' },
          { text: 'Treo bảng', value: 'Treo bảng' },
          { text: 'Cho thuê', value: 'Cho thuê' },
          { text: 'Ngưng cho thuê', value: 'Ngưng cho thuê' }
        ]
      };
    },

    display_columns() {
      if (this.$store.getters.role == 'admin' ||
        (this.$store.getters.permissions && this.$store.getters.permissions['product_sensitive_data']))
      {
        return ProductTableMeta.list_views.columns
      } 
      else {
        return [
            {
                key: 'id',
                variant: 'warning'
            },
            'date',
            'type',
            'street',
            'project',
            'address',
            {
                key: 'gps'
            },
            'price',
            'pricePerArea',
            'priceForRent',
            'notes',
            'area',
            'areaWidth',
            'areaLength',
            'trait',
            'structure',
            'direction'
        ]
      }
      
    }
  },

  mounted() {
    // Initialize filters
    this.initializeFilters();
    // Load mobile data on mount
    this.loadMobileData();
  },

  watch: {
    mobilePage() {
      this.loadMobileData();
    }
  },

  methods: {
    formatDate(date) {
      return moment(date).format('DD/MM/YYYY');
    },

    formatPrice(price) {
      if (!price) return '';
      return parseFloat(price).toLocaleString('vi-VN');
    },

    getStatusClass(status) {
      if (status === 'Đã bán') return 'text-success';
      if (status === 'Đang bán') return 'text-primary';
      if (status === 'Ngưng bán') return 'text-danger';
      if (status === 'Bán hoặc Cho thuê') return 'text-info';
      if (status === 'Treo bảng') return 'text-warning';
      return '';
    },

    getRowVariant(item) {
      if(item.priority > 2) {
        return 'warning';
      } 
      
      if(item.urgency > 2) {
        return 'danger';
      }
      return '';
    },

    async loadMobileData() {
      this.isLoading = true;
      try {
        const service = new ProductService();
        const response = await service.getProducts({
          page: this.mobilePage,
          limit: this.mobilePagination.limit,
          filters: this.filters
        });
        this.mobileData = (response.data || []).map(item => {
          // Apply the same preprocessing as the table view
          return this.rowPreprocessFunc ? this.rowPreprocessFunc(item) : item;
        });
        this.mobilePagination.total = response.total || 0;
      } catch (error) {
        console.error('Error loading mobile data:', error);
        // Fallback: use empty data
        this.mobileData = [];
        this.mobilePagination.total = 0;
      } finally {
        this.isLoading = false;
      }
    },

    can_user_edit () {
      if (this.$store.getters.permissions && this.$store.getters.permissions['product_input']) {
        return true;
      }

      return false;
    },

    rowClicked (event) {
        let routeData = this.$router.resolve({path: `/v2/products/${event.id}/view`});
        window.open(routeData.href, '_blank');
        // this.$router.push({ path: `/v2/products/${event.id}` }) 
    },

    rowEditClicked (event) {
      if(!this.can_user_edit()) {
        alert('Bạn không có quyền thực hiện tính năng này');
        return;
      }

        let routeData = this.$router.resolve({path: `/v2/products/${event.id}`});
        window.open(routeData.href, '_blank');
        // this.$router.push({ path: `/v2/products/${event.id}` }) 
    },

    async rowDeleteClicked (obj) {
      if(!this.can_user_edit()) {
        alert('Bạn không có quyền thực hiện tính năng này');
        return;
      }
      
      var r = confirm("Thao tác xoá sẽ không thể phục hồi lại. Bạn có muốn xoá dữ liệu?");
      if (r == true) {
          
      } else {
          return;
      }

      this.isLoading = true;

      var service = new ProductService();
      await service.deleteProductById(obj.id);

      this.isLoading = false;

      this.loadMobileData();
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(false);
      }
    },

    async updateGroupClicked (obj) {
      if(!this.can_user_edit()) {
        alert('Bạn không có quyền thực hiện tính năng này');
        return;
      }

      var r = confirm("Thao tác cập nhật không thể phục hồi lại. Bạn có muốn?");
      if (r == true) {
          
      } else {
          return;
      }

      this.isLoading = true;

      var service = new ProductService();
      if(obj.menu_code == 'status_sold') {
        service.updateGroupProducts(obj.ids, 'status', 'Đã bán', 'text');
        service.updateGroupProducts(obj.ids, 'priority', 2, 'number');
        service.updateGroupProducts(obj.ids, 'urgency', 2, 'number');
      } else if(obj.menu_code == 'status_stop') {
        service.updateGroupProducts(obj.ids, 'status', 'Ngưng bán', 'text');
        service.updateGroupProducts(obj.ids, 'priority', 2, 'number');
        service.updateGroupProducts(obj.ids, 'urgency', 2, 'number');
      } else if(obj.menu_code == 'status_selling') {
        service.updateGroupProducts(obj.ids, 'status', 'Đang bán', 'text');
      } else if(obj.menu_code == 'status_selling_renting') {
        service.updateGroupProducts(obj.ids, 'status', 'Bán hoặc Cho thuê', 'text');
      } else if(obj.menu_code == 'status_hang') {
        service.updateGroupProducts(obj.ids, 'status', 'Treo bảng', 'text');
      } else if(obj.menu_code == 'priority_high') {

        service.updateGroupProducts(obj.ids, 'priority', 3, 'number');
      } else if(obj.menu_code == 'urgency_high') {
        service.updateGroupProducts(obj.ids, 'urgency', 3, 'number');
      }

      this.isLoading = false;

      this.loadMobileData();
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(false);
      }
    },

    async deleteGroupClicked (obj) {
      if(!this.can_user_edit()) {
        alert('Bạn không có quyền thực hiện tính năng này');
        return;
      }

      var r = confirm("Thao tác xoá sẽ không thể phục hồi lại. Bạn có muốn xoá dữ liệu?");
      if (r == true) {
          
      } else {
          return;
      }

      this.isLoading = true;

      var service = new ProductService();
      await service.deleteGroupProducts(obj.ids);

      this.isLoading = false;

      this.loadMobileData();
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(false);
      }
    },

    async updateStar (obj) {
      if(!this.can_user_edit()) {
        alert('Bạn không có quyền thực hiện tính năng này');
        return;
      }

      console.log('updateStar ', obj.id, obj.isfavored);

      this.isLoading = true;

      var service = new ProductService();
      await service.updateProductColumn(obj.id, 'isfavored', obj.isfavored, 'boolean');

      this.isLoading = false;

      this.loadMobileData();
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(false);
      }
    },

    cellClicked (obj) {
      console.log('cellClicked ', obj.key, obj.value);
      if(obj.key === 'gps') {
        var location = obj.value.replace(/\s/g, "");
        // value is the gps
        var url='https://www.google.com/maps/search/?api=1&query='+location;
        window.open(url,'_blank');
      }
    },

    menuClicked (obj) {
      console.log('ProductListByProject::menuClicked ', obj.menu_code, obj);

      if(obj.menu_code == 'view_posting') {
        let routeData = this.$router.resolve({path: `/v2/products/${obj.id}/posting`});
        window.open(routeData.href, '_blank');
        return;
      } 
      else if(obj.menu_code == 'view_detail') {
        let routeData = this.$router.resolve({path: `/v2/products/${obj.id}/view`});
        window.open(routeData.href, '_blank');
        return;
      } 
      else if(!this.can_user_edit()) {
        alert('Bạn không có quyền thực hiện tính năng này');
        return;
      }

      console.log('menuClicked ', obj.menu_code, obj.id);
      var service = new ProductService();

      if(obj.menu_code == 'status_sold') {
        service.updateProductColumn(obj.id, 'status', 'Đã bán', 'text');
        service.updateProductColumn(obj.id, 'priority', 2, 'number');
        service.updateProductColumn(obj.id, 'urgency', 2, 'number');
      } else if(obj.menu_code == 'status_stop') {
        service.updateProductColumn(obj.id, 'status', 'Ngưng bán', 'text');
        service.updateProductColumn(obj.id, 'priority', 2, 'number');
        service.updateProductColumn(obj.id, 'urgency', 2, 'number');
      } else if(obj.menu_code == 'status_selling') {
        service.updateProductColumn(obj.id, 'status', 'Đang bán', 'text');
      } else if(obj.menu_code == 'status_selling_renting') {
        service.updateGroupProducts(obj.ids, 'status', 'Bán hoặc Cho thuê', 'text');
      } else if(obj.menu_code == 'status_hang') {
        service.updateGroupProducts(obj.ids, 'status', 'Treo bảng', 'text');
      } else if(obj.menu_code == 'priority_high') {

        service.updateProductColumn(obj.id, 'priority', 3, 'number');
      } else if(obj.menu_code == 'urgency_high') {
        service.updateProductColumn(obj.id, 'urgency', 3, 'number');
      } else if(obj.menu_code == 'view_posting') {
        let routeData = this.$router.resolve({path: `/v2/products/${obj.id}/posting`});
        window.open(routeData.href, '_blank');
      }  else if(obj.menu_code == 'view_detail') {
        let routeData = this.$router.resolve({path: `/v2/products/${obj.id}/view`});
        window.open(routeData.href, '_blank');
      } 

      this.loadMobileData();
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(false);
      }
    },

    rowPreprocessFunc (item) {
      if(item.priority > 2) {
        item._rowVariant='warning';
      } 
      
      if(item.urgency > 2) {
        item._rowVariant='danger';
      }
      return item;
    },

    clearAllFilters() {
      // Reset to first page when applying filters
      this.mobilePage = 1;
      this.loadMobileData();
      
      // Also refresh desktop table if it exists
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(true);
      }
      
      // Close the drawer
      this.closeFilterDrawer();
    },

    applyFilters() {
      // Reset to first page when applying filters
      this.mobilePage = 1;
      this.loadMobileData();
      
      // Also refresh desktop table if it exists
      if (this.$refs.tableview) {
        this.$refs.tableview.refreshData(true);
      }
      
      // Close the drawer
      this.closeFilterDrawer();
    },

    initializeFilters() {
      // Ensure all filters have proper initial values
      this.filters.forEach(filter => {
        if (filter.type === 'multi-select' && !filter.value) {
          filter.value = [];
        }
        if (filter.type === 'number') {
          if (!filter.from) filter.from = null;
          if (!filter.to) filter.to = null;
        }
        if (filter.type === 'boolean' && filter.value === undefined) {
          filter.value = false;
        }
      });
    },

    openFilterDrawer() {
      console.log('Opening filter drawer...');
      this.showFilterDrawer = true;
    },

    closeFilterDrawer() {
      console.log('Closing filter drawer...');
      this.showFilterDrawer = false;
    }
  },
  props: []
}
</script>

<style scoped>
.product-list-by-project {
  padding: 1rem;
  width: 100%;
  min-height: 100vh;
  background-color: #f8f9fa;
  box-sizing: border-box;
  overflow-x: hidden; /* Prevent horizontal scrolling on main container */
}

.product-list-by-project .container-fluid {
  padding: 0;
  max-width: 100%;
}

/* Mobile Styles */
.mobile-view {
  padding: 0;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
}

.mobile-filters {
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  padding: 10px 0;
}

.mobile-summary {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.mobile-cards {
  margin-bottom: 20px;
}

.mobile-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: all 0.2s ease;
}

.mobile-card:hover {
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  transform: translateY(-1px);
}

.mobile-card.warning {
  border-left: 4px solid #ffc107;
}

.mobile-card.danger {
  border-left: 4px solid #dc3545;
}

.mobile-card .card-header {
  padding: 12px 15px;
  border-bottom: 1px solid #dee2e6;
  background: #f8f9fa;
}

.mobile-card .card-title {
  flex: 1;
  min-width: 0;
}

.mobile-card .card-title strong {
  color: #495057;
  font-size: 1rem;
}

.mobile-card .card-title small {
  font-size: 0.875rem;
  color: #6c757d;
  word-break: break-word;
}

.mobile-card .card-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.mobile-card .card-body {
  padding: 15px;
}

.mobile-card .info-row {
  display: flex;
  margin-bottom: 8px;
  align-items: flex-start;
}

.mobile-card .info-row:last-child {
  margin-bottom: 0;
}

.mobile-card .label {
  font-weight: 600;
  color: #495057;
  min-width: 100px;
  flex-shrink: 0;
  font-size: 0.875rem;
}

.mobile-card .value {
  color: #6c757d;
  font-size: 0.875rem;
  word-break: break-word;
  flex: 1;
}

.mobile-card .card-footer {
  padding: 12px 15px;
  border-top: 1px solid #dee2e6;
  background: #f8f9fa;
}

.mobile-card .card-footer .btn {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.mobile-pagination {
  padding: 20px 0;
}

.mobile-pagination .pagination {
  margin-bottom: 0;
}

.mobile-pagination .page-link {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

/* Loading and empty states */
.mobile-loading,
.mobile-empty {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.mobile-loading .spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Force mobile/desktop view based on screen size */
@media (max-width: 768px) {
  .desktop-view {
    display: none !important;
  }
  
  .mobile-view {
    display: block !important;
  }
  
  /* Ensure mobile view takes full width */
  .product-list-by-project .mobile-view {
    width: 100% !important;
    max-width: 100% !important;
    padding: 0 !important;
  }
}

@media (min-width: 769px) {
  .mobile-view {
    display: none !important;
  }
  
  .desktop-view {
    display: block !important;
  }
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .mobile-view {
    padding: 0;
  }
  
  .mobile-card .card-header,
  .mobile-card .card-body,
  .mobile-card .card-footer {
    padding: 10px;
  }
  
  .mobile-card .label {
    min-width: 80px;
    font-size: 0.8rem;
  }
  
  .mobile-card .value {
    font-size: 0.8rem;
  }
  
  .mobile-card .card-footer .btn {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }
  
  .mobile-card .card-footer .d-flex {
    flex-wrap: wrap;
    gap: 5px;
  }
  
  .mobile-card .card-footer .btn {
    flex: 1;
    min-width: calc(50% - 2.5px);
  }
}

/* Utility classes */
.clickable {
  cursor: pointer;
}

.text-success {
  color: #28a745 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-primary {
  color: #007bff !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-muted {
  color: #6c757d !important;
}

/* Animation for card appearance */
.mobile-card {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Badge styling for active filters */
.mobile-filters .badge,
.desktop-filters .badge {
  font-size: 0.7rem;
  padding: 0.2em 0.4em;
}

.desktop-filters {
  display: flex;
  justify-content: flex-start;
  align-items: center;
}

/* Hide desktop filter button on mobile */
@media (max-width: 768px) {
  .desktop-filters {
    display: none !important;
  }
}

/* Desktop view improvements */
.desktop-view {
  width: 100%;
  max-width: 100%;
  padding: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  overflow: hidden;
}

.desktop-view .table-responsive {
  border: none;
  margin: 0;
  padding: 0;
  overflow-x: auto;
  overflow-y: visible;
  width: 100%;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
}

.desktop-view .table {
  margin-bottom: 0;
  width: auto;
  min-width: 100%;
  table-layout: auto;
  white-space: nowrap;
}

.desktop-view .table th,
.desktop-view .table td {
  white-space: nowrap;
  padding: 0.75rem 1rem;
  vertical-align: middle;
  border-right: 1px solid #dee2e6;
  min-width: 120px;
}

.desktop-view .table th:last-child,
.desktop-view .table td:last-child {
  border-right: none;
}

/* Specific minimum widths for important columns */
.desktop-view .table th:nth-child(1), /* Actions */
.desktop-view .table td:nth-child(1) {
  min-width: 100px;
  text-align: center;
}

.desktop-view .table th:nth-child(7), /* Address */
.desktop-view .table td:nth-child(7) {
  min-width: 250px;
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.desktop-view .table th:nth-child(12), /* Notes */
.desktop-view .table td:nth-child(12) {
  min-width: 200px;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.desktop-view .table th {
  background-color: #343a40;
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  border: 1px solid #454d55;
}

.desktop-view .table td {
  border: 1px solid #dee2e6;
  font-size: 0.875rem;
}

/* Improve table container */
.desktop-view .card {
  border: none;
  box-shadow: none;
  margin: 0;
  padding: 0;
}

.desktop-view .card-body {
  padding: 0;
  margin: 0;
}

/* Ensure proper spacing for controls */
.desktop-view .mb-3 {
  margin-bottom: 1rem !important;
}

/* Improve pagination layout */
.desktop-view .d-flex.justify-content-between {
  margin: 1rem 0;
  padding: 0.5rem 0;
  border-top: 1px solid #dee2e6;
}

/* Better responsive behavior for large screens */
@media (min-width: 1400px) {
  .desktop-view .table {
    width: 100%;
  }
}

/* Ensure table always scrolls when needed */
.desktop-view .table-responsive {
  display: block;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.desktop-view .table {
  display: table;
  width: max-content;
  min-width: 100%;
}

/* Improve scrollbar appearance */
.desktop-view .table-responsive::-webkit-scrollbar {
  height: 12px;
}

.desktop-view .table-responsive::-webkit-scrollbar-track {
  background: #f8f9fa;
  border-radius: 6px;
  margin: 0 10px;
}

.desktop-view .table-responsive::-webkit-scrollbar-thumb {
  background: #6c757d;
  border-radius: 6px;
  border: 2px solid #f8f9fa;
}

.desktop-view .table-responsive::-webkit-scrollbar-thumb:hover {
  background: #495057;
}

/* Force scrollbar to always show when content overflows */
.desktop-view .table-responsive {
  scrollbar-width: thin;
  scrollbar-color: #6c757d #f8f9fa;
}

/* Ensure proper spacing and prevent cutoff */
.desktop-view {
  margin-bottom: 2rem;
}

.desktop-view .table-responsive {
  margin-bottom: 0;
  border-radius: 0 0 8px 8px;
  padding: 0;
}

/* Improve empty state */
.desktop-view .table tbody:empty::after {
  content: "Không có dữ liệu";
  display: block;
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-style: italic;
}

/* Better button spacing */
.desktop-filters {
  margin-bottom: 1rem;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* Ensure pagination is properly spaced */
.desktop-view .pagination {
  margin: 1rem 0;
}
</style>
